/**
 * Getting text out of a file the user picked.
 *
 * All of it happens on the device. No file is uploaded, and no third-party
 * service is contacted -- there is no network call anywhere in this module.
 *
 * What works, honestly stated:
 *   - plain text and CSV: read directly;
 *   - PDFs: text is pulled out of the page content streams, which covers the
 *     PDF receipts that MAE, Maybank and TNG produce (they are real text, not
 *     scans). A scanned PDF has no text to find, and this module says so
 *     rather than returning an empty string and pretending;
 *   - images: the browser has no bundled OCR here, and shipping a 10MB OCR
 *     model would make the app heavier than the problem deserves. On iPhone
 *     the platform already solves this better: Live Text reads a screenshot in
 *     Photos, and the text can be pasted straight into the receipt box. Where a
 *     browser does expose a text detector, it is used, with progress and a
 *     cancel button.
 */

export interface ExtractionProgress {
  message: string;
  /** 0 to 1. */
  ratio: number;
}

export interface ExtractionOptions {
  onProgress?: (progress: ExtractionProgress) => void;
  /** Set `cancelled` to true to stop a long extraction. */
  signal?: { cancelled: boolean };
}

export interface ExtractionResult {
  text: string;
  /** Set when nothing could be extracted, explaining what to do instead. */
  reason?: string;
  kind: 'text' | 'pdf' | 'image';
}

const MAX_BYTES = 20 * 1024 * 1024;

export async function extractTextFromFile(
  file: File,
  options: ExtractionOptions = {},
): Promise<ExtractionResult> {
  if (file.size > MAX_BYTES) {
    throw new Error('That file is larger than 20 MB. Try pasting the receipt text instead.');
  }

  const type = file.type || guessTypeFromName(file.name);

  if (type.startsWith('text/') || /\.(txt|csv|md)$/i.test(file.name)) {
    options.onProgress?.({ message: 'Reading file', ratio: 0.5 });
    return { text: await file.text(), kind: 'text' };
  }

  if (type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    return extractFromPdf(file, options);
  }

  if (type.startsWith('image/')) {
    return extractFromImage(file, options);
  }

  throw new Error('That file type is not supported. Use a screenshot, a PDF, or plain text.');
}

function guessTypeFromName(name: string): string {
  if (/\.pdf$/i.test(name)) return 'application/pdf';
  if (/\.(png|jpe?g|heic|heif|webp|gif)$/i.test(name)) return 'image/*';
  if (/\.(txt|csv|md)$/i.test(name)) return 'text/plain';
  return '';
}

/* ---------- PDF ---------- */

async function inflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  // Two wrappers are possible: zlib ("deflate") and bare ("deflate-raw").
  for (const format of ['deflate', 'deflate-raw'] as const) {
    try {
      const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream(format));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    } catch {
      // Try the next wrapper.
    }
  }
  return null;
}

/** Pull the readable strings out of a PDF's content streams. */
async function extractFromPdf(
  file: File,
  options: ExtractionOptions,
): Promise<ExtractionResult> {
  options.onProgress?.({ message: 'Opening PDF', ratio: 0.1 });

  const bytes = new Uint8Array(await file.arrayBuffer());
  const latin1 = new TextDecoder('latin1').decode(bytes);

  const streams: Uint8Array[] = [];
  const pattern = /stream\r?\n?/g;
  let match = pattern.exec(latin1);

  while (match) {
    const start = match.index + match[0].length;
    const end = latin1.indexOf('endstream', start);
    if (end === -1) break;
    streams.push(bytes.subarray(start, end));
    pattern.lastIndex = end;
    match = pattern.exec(latin1);
    if (streams.length > 500) break;
  }

  const pieces: string[] = [];

  for (const [index, stream] of streams.entries()) {
    if (options.signal?.cancelled) throw new Error('Cancelled.');
    options.onProgress?.({
      message: `Reading PDF (${index + 1} of ${streams.length})`,
      ratio: 0.1 + (0.8 * (index + 1)) / Math.max(1, streams.length),
    });

    const header = new TextDecoder('latin1').decode(stream.subarray(0, 2));
    // 0x78 is the zlib magic byte; anything else is probably already plain.
    const content =
      header.charCodeAt(0) === 0x78 ? await inflate(stream) : stream;
    if (!content) continue;

    const text = new TextDecoder('latin1').decode(content);
    if (!/\bTj\b|\bTJ\b/.test(text)) continue;
    pieces.push(readTextOperators(text));

    // Yield so the progress bar can actually paint.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  options.onProgress?.({ message: 'Finishing', ratio: 1 });

  const text = pieces.join('\n').replace(/\n{3,}/g, '\n\n').trim();

  if (!text) {
    return {
      text: '',
      kind: 'pdf',
      reason:
        'No text was found in that PDF. It is probably a scan rather than a text receipt. On iPhone you can open it, select the text with Live Text, and paste it into the box above.',
    };
  }

  return { text, kind: 'pdf' };
}

/**
 * Read the string arguments of PDF text operators.
 * `(Kopi Kita) Tj` and `[(RM ) -20 (12.90)] TJ` both carry visible text.
 */
function readTextOperators(content: string): string {
  const lines: string[] = [];
  let current = '';

  const tokens = content.match(/\((?:\\.|[^\\()])*\)|\bTJ\b|\bTj\b|\bTd\b|\bTD\b|\bT\*\b|\bET\b/g);
  if (!tokens) return '';

  for (const token of tokens) {
    if (token.startsWith('(')) {
      current += decodePdfString(token.slice(1, -1));
      continue;
    }
    // Any positioning or end-of-text operator ends the visual line.
    if (token === 'Td' || token === 'TD' || token === 'T*' || token === 'ET') {
      if (current.trim()) lines.push(current.trim());
      current = '';
    }
  }

  if (current.trim()) lines.push(current.trim());
  return lines.join('\n');
}

function decodePdfString(value: string): string {
  return value
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
    .replace(/\\([0-7]{1,3})/g, (_match, octal: string) =>
      String.fromCharCode(Number.parseInt(octal, 8)),
    )
    .replace(/\\(.)/g, '$1');
}

/* ---------- images ---------- */

interface TextDetectorLike {
  detect(source: ImageBitmapSource): Promise<{ rawValue: string }[]>;
}

/**
 * Try the browser's own text detector, which a few engines expose. When it is
 * not there -- which includes every iPhone today -- say so plainly and point at
 * Live Text, which does the job better anyway.
 */
async function extractFromImage(
  file: File,
  options: ExtractionOptions,
): Promise<ExtractionResult> {
  const DetectorConstructor = (
    globalThis as unknown as { TextDetector?: new () => TextDetectorLike }
  ).TextDetector;

  if (!DetectorConstructor) {
    return {
      text: '',
      kind: 'image',
      reason:
        'This browser cannot read text from an image, and the app does not bundle an OCR engine. On iPhone: open the screenshot in Photos, press and hold the text, choose Select All, then Copy, and paste it into the box above. Live Text does this better than anything the app could ship.',
    };
  }

  options.onProgress?.({ message: 'Looking for text in the image', ratio: 0.3 });

  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    if (options.signal?.cancelled) throw new Error('Cancelled.');

    options.onProgress?.({ message: 'Reading text', ratio: 0.7 });
    const detector = new DetectorConstructor();
    const blocks = await detector.detect(bitmap);
    options.onProgress?.({ message: 'Finishing', ratio: 1 });

    const text = blocks.map((block) => block.rawValue).join('\n').trim();
    if (!text) {
      return {
        text: '',
        kind: 'image',
        reason: 'No text was found in that image. Try pasting the receipt text instead.',
      };
    }
    return { text, kind: 'image' };
  } finally {
    bitmap?.close();
  }
}
